import { Mesh, type MeshBasicMaterial, Raycaster, Vector2 } from 'three';

import type { RotationGizmoManager } from '../rotation-gizmo-manager.class';
import { beginDragCfn, endDragCfn, updateDragCfn } from './handle-drag.cfn';

/**
 * Sets up mouse event listeners for gizmo interaction:
 * - mousedown on a ring starts tracking the drag
 * - mousemove during drag rotates the appliance
 * - mouseup ends the drag and emits rotation:complete
 * - click on non-gizmo objects deactivates the gizmo
 */
export const setupGizmoEventsCfn = (self: RotationGizmoManager, _params: Record<string, never>): void => {
	const config = self.internalConfig;
	const { domElement, camera } = config;

	const raycaster = new Raycaster();
	const pointer = new Vector2();

	/**
	 * Finds a gizmo ring mesh under the mouse pointer
	 */
	const findGizmoRingAtPointer = (clientX: number, clientY: number): Mesh | null => {
		const rings = self.internalGizmoRings;
		if (!rings || rings.length === 0) return null;

		const rect = domElement.getBoundingClientRect();
		pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
		pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;

		raycaster.setFromCamera(pointer, camera);

		const intersects = raycaster.intersectObjects(rings, false);
		if (intersects.length > 0) {
			return intersects[0].object as Mesh;
		}
		return null;
	};

	/**
	 * Highlights the hovered ring by increasing opacity
	 */
	const setRingHighlight = (ring: Mesh | null): void => {
		const rings = self.internalGizmoRings;
		if (!rings) return;

		for (const r of rings) {
			const mat = r.material as MeshBasicMaterial;
			mat.opacity = r === ring ? 0.8 : 0.5;
		}
	};

	// ── Mouse Down ──────────────────────────────────────────────────────────
	const handleMouseDown = (event: MouseEvent): void => {
		if (!self.isActive) return;

		const ring = findGizmoRingAtPointer(event.clientX, event.clientY);
		if (!ring) return;

		// Prevent default to avoid text selection during drag
		event.preventDefault();

		const axis = ring.userData.axis as 'x' | 'y' | 'z';
		self.internalDragAxis = axis;
		self.internalIsDragging = true;

		// Capture the target object's current rotation before dragging
		const targetObject = self.internalTargetObject;
		if (targetObject) {
			self.internalInitialTargetRotation = {
				x: targetObject.rotation.x,
				y: targetObject.rotation.y,
				z: targetObject.rotation.z
			};
		}

		beginDragCfn(self, { screenX: event.clientX, screenY: event.clientY });
	};
	self.internalHandleMouseDown = handleMouseDown;

	// ── Mouse Move ──────────────────────────────────────────────────────────
	const handleMouseMove = (event: MouseEvent): void => {
		if (!self.isActive) return;

		if (self.internalIsDragging) {
			updateDragCfn(self, { screenX: event.clientX, screenY: event.clientY });
			return;
		}

		// Hover highlight when not dragging
		const ring = findGizmoRingAtPointer(event.clientX, event.clientY);
		setRingHighlight(ring);

		// Set cursor style
		domElement.style.cursor = ring ? 'grab' : '';
	};
	self.internalHandleMouseMove = handleMouseMove;

	// ── Mouse Up ────────────────────────────────────────────────────────────
	const handleMouseUp = (_event: MouseEvent): void => {
		if (!self.internalIsDragging) return;

		self.internalIsDragging = false;
		endDragCfn(self);

		domElement.style.cursor = '';
	};
	self.internalHandleMouseUp = handleMouseUp;

	// ── Click (for deactivation) ────────────────────────────────────────────
	// Click fires after mousedown+mouseup. If the click was NOT on a gizmo ring
	// and we were NOT dragging, deactivate the gizmo.
	const handleClick = (event: MouseEvent): void => {
		if (!self.isActive) return;
		// If we just finished a drag, skip this click event
		if (self.internalJustFinishedDrag) {
			self.internalJustFinishedDrag = false;
			return;
		}

		const ring = findGizmoRingAtPointer(event.clientX, event.clientY);
		if (!ring) {
			// Clicked somewhere that is not a gizmo ring -- deactivate
			self.deactivate();
		}
	};
	self.internalHandleClick = handleClick;

	// Attach listeners
	domElement.addEventListener('mousedown', handleMouseDown);
	domElement.addEventListener('mousemove', handleMouseMove);
	window.addEventListener('mouseup', handleMouseUp);
	// Use capture phase so gizmo click handler runs before the engine's click handler
	domElement.addEventListener('click', handleClick, true);
};
